import { initAsideDrawer } from './../js/initAsideDrawer';
import { initScrollSpyActiveNav } from './../js/initScrollSpyActiveNav';

(() => {
  const DURATION_MS = 220; // має збігатися з CSS transition height
  const HEADER_OFFSET = 72; // sticky header height for anchor scroll
  const DESKTOP_MIN = 1024;

  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () => window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`).matches;

  const getAll = (selector, root = document) =>
    Array.from(root.querySelectorAll(selector));

  const getOne = (selector, root = document) =>
    root.querySelector(selector);

  const generateId = (() => {
    let counter = 0;
    return (prefix = 'id') =>
      `${prefix}-${Date.now().toString(36)}-${(++counter).toString(36)}`;
  })();

  /* =========================
   * Accordion helpers
   * ========================= */

  const getCurrentHeightPx = (el) => `${el.getBoundingClientRect().height}px`;

  const clearTimersAndHandlers = (panel) => {
    if (panel.__faqTimer) {
      clearTimeout(panel.__faqTimer);
      panel.__faqTimer = null;
    }
    if (panel.__faqOnEnd) {
      panel.removeEventListener('transitionend', panel.__faqOnEnd);
      panel.__faqOnEnd = null;
    }
  };

  function togglePanel(itemEl, button, panel, open, { animate = true } = {}) {
    clearTimersAndHandlers(panel);

    button.setAttribute('aria-expanded', String(open));

    if (!animate || prefersReducedMotion()) {
      itemEl.classList.toggle('is-open', open);
      panel.hidden = !open;
      panel.style.height = '';
      panel.style.overflow = '';
      return;
    }

    panel.style.overflow = 'hidden';

    if (open) {
      panel.hidden = false;
      itemEl.classList.add('is-open');

      panel.style.height = '0px';
      panel.offsetHeight; // reflow
      panel.style.height = `${panel.scrollHeight}px`;

      const onEnd = (e) => {
        if (e.target !== panel) return;
        if (button.getAttribute('aria-expanded') !== 'true') return;

        panel.style.height = '';
        panel.style.overflow = '';
        clearTimersAndHandlers(panel);
      };

      panel.__faqOnEnd = onEnd;
      panel.addEventListener('transitionend', onEnd);
    } else {
      itemEl.classList.remove('is-open');

      panel.style.height = getCurrentHeightPx(panel);
      panel.offsetHeight; // reflow
      panel.style.height = '0px';

      panel.__faqTimer = setTimeout(() => {
        if (button.getAttribute('aria-expanded') === 'false') {
          panel.hidden = true;
          panel.style.height = '';
          panel.style.overflow = '';
        }
        clearTimersAndHandlers(panel);
      }, DURATION_MS + 40);

      const onEnd = (e) => {
        if (e.target !== panel) return;
        if (button.getAttribute('aria-expanded') !== 'false') return;

        panel.hidden = true;
        panel.style.height = '';
        panel.style.overflow = '';
        clearTimersAndHandlers(panel);
      };

      panel.__faqOnEnd = onEnd;
      panel.addEventListener('transitionend', onEnd);
    }
  }

  function closeOtherPanels(items, currentIndex) {
    items.forEach((item, index) => {
      if (index === currentIndex) return;
      if (item.button.getAttribute('aria-expanded') === 'true') {
        togglePanel(item.faqItem, item.button, item.panel, false, { animate: true });
      }
    });
  }

  function initFaqAccordion(container) {
    if (container.__faqInited) return;
    container.__faqInited = true;

    const singleOpen = container.getAttribute('data-faq-single') === 'true';

    const items = getAll('.faq-page__item', container)
      .map((faqItem) => {
        if (faqItem.closest('[inert]')) return null;
        const button = getOne('.faq-page__item-question', faqItem);
        const panel = getOne('.faq-page__item-answer', faqItem);
        if (!button || !panel) return null;

        if (!button.id) button.id = generateId('faq-btn');
        if (!panel.id) panel.id = generateId('faq-panel');

        button.setAttribute('aria-controls', panel.id);
        button.setAttribute('aria-expanded', button.getAttribute('aria-expanded') || 'false');

        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', button.id);

        const initiallyOpen = button.getAttribute('aria-expanded') === 'true';
        panel.hidden = !initiallyOpen;
        faqItem.classList.toggle('is-open', initiallyOpen);

        return { faqItem, button, panel };
      })
      .filter(Boolean);

    if (!items.length) return;

    items.forEach((item, index) => {
      item.button.addEventListener('click', () => {
        const isOpen = item.button.getAttribute('aria-expanded') === 'true';
        const nextState = !isOpen;

        if (singleOpen && nextState) closeOtherPanels(items, index);

        togglePanel(item.faqItem, item.button, item.panel, nextState, { animate: true });
      });
    });
  }

  /* =========================
   * Boot
   * ========================= */

  function boot() {
    getAll('[data-faq], .faq-page__items').forEach(initFaqAccordion);

    initAsideDrawer({
      headerOffset: HEADER_OFFSET,
      desktopMin: DESKTOP_MIN,
      selectors: {
        toggle: '.faq-page__toggle',
        aside: '.faq-page__asside',
        close: '.faq-page__asside-top-close',
        navLink: '.faq-page__nav-link',
        navItem: '.faq-page__nav-item',
      },
      classes: {
        open: 'is-open',
        bodyLocked: 'overflow-hidden',
        active: 'active',
      },
    });

    initScrollSpyActiveNav({
      headerOffset: HEADER_OFFSET,
      buffer: 100,
      desktopMin: DESKTOP_MIN,
      asideSelector: '.faq-page__asside',
      navLinkSelector: '.faq-page__nav-link',
      navItemSelector: '.faq-page__nav-item',
      activeClass: 'active',
      respectHashOnInit: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('load', boot, { once: true });
  document.addEventListener('shopify:section:load', boot);
})();
